import { type FunctionComponent, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
  Button,
  Spinner,
  Text,
  makeStyles,
  shorthands,
  tokens,
} from '@fluentui/react-components';
import { CheckmarkCircle24Filled } from '@fluentui/react-icons';
import { formatDate } from '@moko/utils';
import {
  getMySupportTickets,
  type ISupportTicket,
} from '../services/supportService';
import useFetch from '../hooks/useFetch';
import type { IPagedCollection } from '../DataGrid/PagedDataGrid';

const PAGE_SIZE = 20;

const useStyles = makeStyles({
  root: {
    display: 'flex',
    flexDirection: 'column',
    ...shorthands.gap(tokens.spacingVerticalM),
  },
  centered: {
    display: 'flex',
    justifyContent: 'center',
    ...shorthands.padding(tokens.spacingVerticalXXL),
  },
  ticket: {
    display: 'flex',
    flexDirection: 'column',
    ...shorthands.gap(tokens.spacingVerticalXS),
  },
  card: {
    display: 'flex',
    ...shorthands.gap(tokens.spacingHorizontalM),
    ...shorthands.padding(tokens.spacingVerticalM, tokens.spacingHorizontalM),
    ...shorthands.borderRadius(tokens.borderRadiusMedium),
    backgroundColor: tokens.colorStatusSuccessBackground1,
    ...shorthands.border('1px', 'solid', tokens.colorStatusSuccessBorder1),
  },
  icon: {
    color: tokens.colorStatusSuccessForeground1,
    flexShrink: 0,
  },
  cardBody: {
    display: 'flex',
    flexDirection: 'column',
    ...shorthands.gap(tokens.spacingVerticalXXS),
  },
  title: {
    fontWeight: tokens.fontWeightSemibold,
    color: tokens.colorStatusSuccessForeground1,
  },
  reference: {
    color: tokens.colorStatusSuccessForeground1,
  },
  submitted: {
    color: tokens.colorNeutralForeground3,
    fontSize: tokens.fontSizeBase200,
  },
});

const PreviousTickets: FunctionComponent = () => {
  const { t } = useTranslation();
  const classes = useStyles();
  const [pageNo, setPageNo] = useState(1);
  const [tickets, setTickets] = useState<ISupportTicket[]>([]);

  const { data, loading, error } = useFetch<IPagedCollection<ISupportTicket>>({
    queryKey: [pageNo],
    queryFn: (abortController) =>
      getMySupportTickets(abortController, pageNo, PAGE_SIZE),
  });

  useEffect(() => {
    if (!data) return;
    setTickets((prev) =>
      pageNo === 1 ? data.items : [...prev, ...data.items]
    );
  }, [data, pageNo]);

  const isLastPage = data?.isLastPage ?? true;
  const loadingFresh = loading && pageNo === 1;
  const loadingMore = loading && pageNo > 1;

  if (loadingFresh) {
    return (
      <div className={classes.centered}>
        <Spinner label={t('loadingTickets', 'Loading tickets...')} />
      </div>
    );
  }

  if (error && tickets.length === 0) {
    return (
      <div className={classes.centered}>
        <Text>
          {t(
            'failedToLoadTickets',
            'Failed to load tickets. Please try again.'
          )}
        </Text>
      </div>
    );
  }

  if (tickets.length === 0) {
    return (
      <div className={classes.centered}>
        <Text>{t('noPreviousTickets', 'No previous tickets yet.')}</Text>
      </div>
    );
  }

  return (
    <div className={classes.root}>
      {tickets.map((ticket) => (
        <div key={ticket.id ?? ticket.ticketNumber} className={classes.ticket}>
          <div className={classes.card}>
            <CheckmarkCircle24Filled className={classes.icon} />
            <div className={classes.cardBody}>
              <Text className={classes.title}>
                {ticket.subject || t('requestSubmitted', 'Request submitted')}
              </Text>
              <Text>
                {t(
                  'ticketCreatedConfirmation',
                  'Your support ticket has been created.'
                )}
              </Text>
              {ticket.ticketNumber && (
                <Text className={classes.reference}>
                  {t('referenceNumber', 'Reference number: {{number}}', {
                    number: ticket.ticketNumber,
                  })}
                </Text>
              )}
            </div>
          </div>
          {ticket.createdAt && (
            <Text className={classes.submitted}>
              {t('submittedAt', 'Submitted: {{date}}', {
                date: formatDate(ticket.createdAt, true, true),
              })}
            </Text>
          )}
        </div>
      ))}

      {loadingMore && (
        <div className={classes.centered}>
          <Spinner label={t('loadingTickets', 'Loading tickets...')} />
        </div>
      )}

      {!isLastPage && !loadingMore && (
        <Button appearance="secondary" onClick={() => setPageNo((p) => p + 1)}>
          {t('loadMore', 'Load more')}
        </Button>
      )}
    </div>
  );
};

export default PreviousTickets;
